// «Citas por asignar» (founder 21-oct-2026) — citas sin cuenta cuyo nombre coincide con UNA ficha del barbero conectada
// a una cuenta. Nunca se ligan solas: el barbero confirma la sugerencia o elige otro cliente con el selector.
import { useCallback, useEffect, useState } from 'react';
import { UserCheck, ChevronDown, Loader2, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { bookingApi } from '@/lib/bookingApi';
import { friendlyError } from '@/lib/apiError';
import { tr } from '@/contexts/LanguageContext';
import { formatDateShortEs, formatTime12 } from '@/utils/bookingFormat';
import { AssignClientPicker } from '@/components/Bookings/AssignClientPicker';

export const PendingAssignCard = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const [picking, setPicking] = useState(null);
  const load = useCallback(() => bookingApi.pendingAssign().then(setData).catch(() => setData({ total: 0, items: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!data || data.total === 0) return null;

  const confirm = async (it) => {
    setBusy(it.booking_id);
    try {
      const r = await bookingApi.confirmAssign(it.booking_id, it.suggested.id);
      toast.success(r.linked_account ? tr("Cita asignada: ya le aparece en su app") : tr("Cita asignada a la ficha"));
      await load(); onChanged?.();
    } catch (e) { toast.error(friendlyError(e, tr("No pudimos asignar el cliente"))); } finally { setBusy(null); }
  };

  return (
    <section className="mb-4 bg-amber-500/5 border border-amber-500/30 rounded-2xl p-3.5" data-testid="pending-assign-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="pending-assign-toggle">
        <UserCheck className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="flex-1 text-sm font-semibold text-foreground">{tr("Citas por asignar")} <span className="text-amber-400 tabular-nums" data-testid="pending-assign-count">({data.total})</span></span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] text-muted-foreground">{tr("Estas citas tienen un nombre que coincide con un cliente tuyo con cuenta. Confírmalo para que le aparezca en su app y pueda pagar; o elige otro cliente.")}</p>
          {data.items.map((it) => (
            <div key={it.booking_id} className="bg-card border border-border rounded-xl p-3" data-testid={`pending-assign-${it.booking_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{it.client_name} <span className="text-muted-foreground font-normal">· {it.service_name}</span></p>
                  <p className="text-[11px] text-muted-foreground">{formatDateShortEs(it.date)} · {formatTime12(it.start_time)}{it.upcoming ? '' : ` · ${tr("pasada")}`}</p>
                  <p className="text-[11px] text-primary mt-1 inline-flex items-center gap-1" data-testid={`pending-assign-suggested-${it.booking_id}`}>
                    <BadgeCheck className="w-3 h-3" /> {tr("Sugerido")}: {it.suggested.name}{it.suggested.phone ? ` · ${it.suggested.phone}` : ''}{it.suggested.email ? ` · ${it.suggested.email}` : ''}
                  </p>
                </div>
              </div>
              {picking === it.booking_id ? (
                <div className="mt-2"><AssignClientPicker bookingId={it.booking_id} onAssigned={() => { setPicking(null); load(); onChanged?.(); }} onClose={() => setPicking(null)} /></div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <button type="button" disabled={busy === it.booking_id} onClick={() => confirm(it)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold py-2 hover:opacity-90 disabled:opacity-60"
                    data-testid={`pending-assign-confirm-${it.booking_id}`}>
                    {busy === it.booking_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />} {tr("Sí, es este cliente")}
                  </button>
                  <button type="button" onClick={() => setPicking(it.booking_id)}
                    className="flex-1 rounded-full border border-border text-xs font-semibold py-2 text-foreground hover:border-primary/50"
                    data-testid={`pending-assign-other-${it.booking_id}`}>{tr("Elegir otro")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
